import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { createStore, get, set, del, clear } from 'idb-keyval';
import type { Query } from '@tanstack/react-query';
import { AuthTokenService } from './auth-token-service';

const DB_NAME = 'getfacade-query-cache';
const STORE_NAME = 'v1';
const CACHE_KEY = 'queries';
const ANON_SCOPE = 'anon';

const idbStore =
  typeof indexedDB !== 'undefined' ? createStore(DB_NAME, STORE_NAME) : null;

// Namespace every IndexedDB entry by the current account. Without this a
// different user can hydrate the previous user's persisted queries
// (`['projects','detail',id]`, albums, estimates…) whenever a clean logout
// didn't run — a silently swapped token, an interrupted logout, or a crash
// between login and the identity-change wipe. The id is read synchronously from
// localStorage, so the very first restore at cold boot already lands in the
// correct per-account bucket; an anonymous boot uses a dedicated `anon` bucket.
function scopedKeyFor(userId: string | null, key: string): string {
  return `${userId ?? ANON_SCOPE}::${key}`;
}

function scopedKey(key: string): string {
  return scopedKeyFor(AuthTokenService.getUserIdSync(), key);
}

const asyncStorage = {
  getItem: async (key: string) => {
    if (!idbStore) return null;
    const value = await get<string>(scopedKey(key), idbStore);
    return value ?? null;
  },
  setItem: async (key: string, value: string) => {
    if (!idbStore) return;
    await set(scopedKey(key), value, idbStore);
  },
  removeItem: async (key: string) => {
    if (!idbStore) return;
    await del(scopedKey(key), idbStore);
  },
};

export const queryPersister = createAsyncStoragePersister({
  storage: asyncStorage,
  key: CACHE_KEY,
  throttleTime: 1000,
});

/**
 * Query keys that must NEVER be persisted to disk.
 * Auth/billing/subscription/payments change often and are security-sensitive —
 * "fail loudly" rules out stale reads from IDB. Job queue and history are
 * session-scoped realtime state, not a cache to seed the next reload.
 */
const VOLATILE_ROOTS = new Set([
  'auth',
  'user',
  'profile',
  'subscriptions',
  'subscription',
  'payments',
  'payment',
  'job-history',
  'jobs',
  'queue',
]);

export function shouldPersistQuery(query: Query): boolean {
  const root = query.queryKey[0];
  if (typeof root !== 'string') return false;
  if (VOLATILE_ROOTS.has(root)) return false;
  // Only persist queries that have actually loaded data successfully.
  return query.state.status === 'success';
}

/**
 * Drop the persisted cache. With a `userId` (logout / identity-change) we wipe
 * just that account's bucket (plus the anonymous one, in case pre-login queries
 * landed there) and leave other accounts' buckets intact — per-account scoping
 * already prevents cross-user hydration, so there's no need to nuke everyone.
 * With no argument we clear the whole store (defensive full wipe, e.g. a
 * version bust).
 */
export async function clearPersistedQueryCache(
  userId?: string | null
): Promise<void> {
  if (!idbStore) return;
  if (userId === undefined) {
    await clear(idbStore);
    return;
  }
  await del(scopedKeyFor(userId, CACHE_KEY), idbStore);
  await del(scopedKeyFor(ANON_SCOPE, CACHE_KEY), idbStore);
}
